/**
 * Единый HTTP-клиент.
 *
 * - baseURL: /api/v1 (Vite proxy в dev, nginx в prod).
 * - credentials: 'include' — cookie sid и csrf передаются всегда.
 * - CSRF: для POST/PUT/PATCH/DELETE (кроме exempt-путей) перед запросом
 *   берётся токен из GET /auth/csrf и кладётся в X-CSRF-Token.
 *   При 403 CSRF_INVALID (или legacy FST_CSRF_INVALID_TOKEN / CSRF_MISMATCH)
 *   токен сбрасывается, перезапрашивается, запрос повторяется один раз.
 * - Ошибки: единый ApiError { status, code, message, details }.
 *
 * Exempt-пути (те же, что в server.ts onRequest requireCsrf):
 *   /auth/login, /auth/set-password, /auth/logout, /registrations.
 */
import axios from "axios";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

const raw = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

const client = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

let csrfToken = null;

const CSRF_EXEMPT = [
  "/auth/login",
  "/auth/set-password",
  "/auth/logout",
  "/registrations",
];

async function ensureCsrf() {
  if (csrfToken) return csrfToken;
  const { data } = await raw.get("/auth/csrf");
  csrfToken = data.token;
  return csrfToken;
}

function isExempt(url) {
  return CSRF_EXEMPT.some((p) => (url || "").startsWith(p));
}

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

client.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  const needsCsrf = !["get", "head", "options"].includes(method);
  if (needsCsrf && !isExempt(config.url)) {
    const token = await ensureCsrf().catch(() => null);
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status ?? 0;
    const body = error.response?.data ?? {};

    if (
      status === 403 &&
      (body.code === "CSRF_INVALID" ||
        body.code === "FST_CSRF_INVALID_TOKEN" ||
        body.code === "CSRF_MISMATCH") &&
      error.config &&
      !error.config.__csrfRetried
    ) {
      csrfToken = null;
      const fresh = await ensureCsrf().catch(() => null);
      if (fresh) {
        error.config.headers = error.config.headers || {};
        error.config.headers["X-CSRF-Token"] = fresh;
        error.config.__csrfRetried = true;
        return client.request(error.config);
      }
    }

    throw new ApiError(
      status,
      body.code || "NETWORK",
      body.message || "Ошибка сети",
      body.details,
    );
  },
);

export const api = {
  get: (url, config) => client.get(url, config).then((r) => r.data),
  post: (url, data, config) =>
    client.post(url, data, config).then((r) => r.data),
  patch: (url, data, config) =>
    client.patch(url, data, config).then((r) => r.data),
  delete: (url, config) => client.delete(url, config).then((r) => r.data),
};
